import api from "@/config/api"
import {
  SYSTEM_STATUS_POLL_INTERVAL,
  SYSTEM_STATUS_FAIL_THRESHOLD,
  SYSTEM_STATUS_AVAILABLE,
  SYSTEM_STATUS_MAINTENANCE,
  SYSTEM_STATUS_UNAVAILABLE
} from "@/constants"

// /up — корневой путь, мимо префикса /api. В dev бьём в хост бэкенда
// (VUE_APP_API_URL), в prod — относительный /up (nginx проксирует на backend).
const HEALTH_BASE_URL = process.env.VUE_APP_API_URL || "/"

// Таймер поллинга держим вне state (не сериализуемый).
let pollTimer = null

// Гард от перекрытия опросов: forcePoll (из axios-интерцептора) и интервал
// могут вызвать check почти одновременно — без него параллельные фейлы
// дважды инкрементят failCount (ломая debounce), а параллельные ответы
// применяют устаревший prev.
let checking = false

// Таймаут одного опроса (меньше интервала) — реализует «timeout → фейл»
// и не даёт зависшему запросу заблокировать гард навсегда.
const CHECK_TIMEOUT = 10000

// Не перезагружаем страницу при восстановлении чаще, чем раз в этот период —
// защита от цикла reload при флапающем (rolling) деплое.
const RELOAD_DEBOUNCE = 30000
const RELOAD_KEY = "system_reloaded_at"

export default {
  namespaced: true,
  state: () => ({
    status: SYSTEM_STATUS_AVAILABLE,
    failCount: 0
  }),
  mutations: {
    setStatus(state, status) {
      state.status = status
    },
    incrementFail(state) {
      state.failCount += 1
    },
    resetFail(state) {
      state.failCount = 0
    }
  },
  actions: {
    // Один опрос /up и маппинг ответа в статус.
    async check({ state, commit, dispatch }) {
      if (checking) return
      checking = true

      const prev = state.status

      try {
        // validateStatus: () => true — любые коды резолвятся (идут в success-
        // интерцептор api.js), поэтому error-интерцептор НЕ дёргает forcePoll
        // на наш же health-запрос (иначе 503/5xx от /up зациклили бы опрос).
        const response = await api.get("/up", {
          baseURL: HEALTH_BASE_URL,
          timeout: CHECK_TIMEOUT,
          validateStatus: () => true
        })

        if (response.status === 200) {
          commit("resetFail")
          dispatch("applyStatus", { next: SYSTEM_STATUS_AVAILABLE, prev })
          return
        }

        if (response.status === 503 && response.data?.status === "maintenance") {
          commit("resetFail")
          dispatch("applyStatus", { next: SYSTEM_STATUS_MAINTENANCE, prev })
          return
        }

        // прочие коды (500/502/504/…) — считаем фейлом
        dispatch("registerFailure", prev)
      } catch (e) {
        // network error / timeout
        dispatch("registerFailure", prev)
      } finally {
        checking = false
      }
    },

    // Debounce: только после FAIL_THRESHOLD подряд уходим в unavailable.
    registerFailure({ state, commit, dispatch }, prev) {
      commit("incrementFail")
      if (state.failCount >= SYSTEM_STATUS_FAIL_THRESHOLD) {
        dispatch("applyStatus", { next: SYSTEM_STATUS_UNAVAILABLE, prev })
      }
    },

    applyStatus({ commit, dispatch }, { next, prev }) {
      commit("setStatus", next)

      const recovered =
        next === SYSTEM_STATUS_AVAILABLE &&
        (prev === SYSTEM_STATUS_MAINTENANCE || prev === SYSTEM_STATUS_UNAVAILABLE)

      if (recovered) {
        dispatch("recover")
      }
    },

    // Сайт вернулся после техработ/сбоя — подтянуть свежий фронт через reload,
    // но не чаще RELOAD_DEBOUNCE, чтобы не зациклиться на флапающем деплое.
    recover() {
      const now = Date.now()
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)

      if (now - last < RELOAD_DEBOUNCE) return

      sessionStorage.setItem(RELOAD_KEY, String(now))
      window.location.reload()
    },

    startPolling({ dispatch }) {
      if (pollTimer) return
      dispatch("check")
      pollTimer = setInterval(() => dispatch("check"), SYSTEM_STATUS_POLL_INTERVAL)
    },

    // Принудительный опрос (вызывается из axios-интерцептора на 5xx).
    forcePoll({ dispatch }) {
      dispatch("check")
    }
  }
}
